import React, { useMemo } from 'react';
import { Modal, Button, Spin, message, Alert } from 'antd';
import { SendOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { invoiceService } from '../services';
import { Invoice } from '../types';
import { formatAmountDots } from '../utils/format';
import { readVietnameseMoney } from '../utils/vietnameseMoneyWords';
import './InvoiceExcelDetailModal.css';

function downloadBlob(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
}

function formatDateShort(value: string | undefined): string {
  if (!value) return '';
  const d = dayjs(value);
  return d.isValid() ? d.format('DD/MM/YYYY') : value;
}

function displayStr(v: string | number | undefined | null): string {
  if (v === undefined || v === null || v === '') return '—';
  return String(v);
}

function parseNum(v: string | number | undefined | null): number | null {
  if (v === undefined || v === null || v === '') return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  const s = String(v).trim().replace(/\s/g, '').replace(/\./g, '').replace(',', '.');
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : null;
}

function buildViewModel(inv: Invoice) {
  const item0 = inv.items?.[0];
  const total = parseFloat(inv.total_amount) || 0;

  const lineSubNum =
    inv.line_subtotal != null
      ? parseNum(inv.line_subtotal)
      : item0?.amount != null
        ? parseNum(item0.amount)
        : null;

  const subGoodsNum =
    inv.subtotal_goods != null
      ? parseNum(inv.subtotal_goods)
      : lineSubNum != null
        ? lineSubNum
        : item0?.amount != null
          ? parseNum(item0.amount)
          : null;

  const taxRate = inv.tax_rate_percent ?? 5;
  let taxAmt = inv.tax_amount != null ? parseNum(inv.tax_amount) : null;
  if (taxAmt == null && subGoodsNum != null) {
    taxAmt = Math.round((subGoodsNum * taxRate) / 100);
  }

  let envFee = inv.environmental_fee_wastewater_sh_10_percent != null
    ? parseNum(inv.environmental_fee_wastewater_sh_10_percent)
    : null;
  if (envFee == null && subGoodsNum != null) {
    envFee = Math.round((subGoodsNum * 10) / 100);
  }

  const usageStart = inv.usage_period_start;
  const usageEnd = inv.usage_period_end;
  let usageRange = '—';
  if (usageStart && usageEnd) {
    usageRange = `${formatDateShort(usageStart)} - ${formatDateShort(usageEnd)}`;
  } else if (usageStart) {
    usageRange = formatDateShort(usageStart);
  } else if (inv.import_date) {
    const m = dayjs(inv.import_date);
    if (m.isValid()) {
      const start = m.startOf('month');
      const end = m.endOf('month');
      usageRange = `${start.format('DD/MM/YYYY')} - ${end.format('DD/MM/YYYY')}`;
    }
  }

  const consumptionRaw =
    inv.consumption_m3 ??
    (item0?.quantity != null ? item0.quantity : undefined);

  const prevRead = inv.previous_month_reading;
  const currRead = inv.current_month_reading;

  const words =
    inv.amount_in_words?.trim() ||
    (total > 0 ? readVietnameseMoney(Math.round(total)) : '—');

  const unitPriceNum = inv.unit_price_detail != null
    ? parseNum(inv.unit_price_detail)
    : item0?.unit_price != null
      ? parseNum(item0.unit_price)
      : null;

  return {
    customerName: inv.customer_name,
    address: displayStr(inv.customer_address),
    taxId: displayStr(inv.customer_tax_code),
    customerCode: displayStr(inv.customer_code ?? inv.invoice_number),
    household: displayStr(inv.household_count),
    quotaBlocks: displayStr(inv.water_quota_m3),
    meterReading: displayStr(inv.meter_block_reading),
    usageRange,
    currRead: displayStr(currRead),
    prevRead: displayStr(prevRead),
    consumptionM3: displayStr(consumptionRaw),
    consQuota: displayStr(inv.consumption_quota_m3 ?? consumptionRaw),
    unitPriceDisplay:
      unitPriceNum != null ? formatAmountDots(unitPriceNum) : '—',
    lineAmountDisplay:
      lineSubNum != null
        ? formatAmountDots(lineSubNum)
        : item0?.amount != null
          ? formatAmountDots(parseNum(item0.amount) ?? 0)
          : '—',
    subGoodsDisplay: subGoodsNum != null ? formatAmountDots(subGoodsNum) : '—',
    taxRate,
    taxDisplay: taxAmt != null ? formatAmountDots(taxAmt) : '—',
    envDisplay: envFee != null ? formatAmountDots(envFee) : '—',
    totalDisplay: total > 0 ? formatAmountDots(Math.round(total)) : '—',
    amountInWords: words,
  };
}

interface InvoiceExcelDetailModalProps {
  visible: boolean;
  invoiceId: number | null;
  onClose: () => void;
}

const InvoiceExcelDetailModal: React.FC<InvoiceExcelDetailModalProps> = ({
  visible,
  invoiceId,
  onClose,
}) => {
  const { data: invoiceResponse, isLoading, isFetching, isError } = useQuery({
    queryKey: ['invoice-detail', invoiceId],
    queryFn: () => {
      if (!invoiceId) throw new Error('No invoice ID');
      return invoiceService.getDetail(invoiceId);
    },
    enabled: !!invoiceId && visible,
  });

  const invoice = invoiceResponse?.data;

  const vm = useMemo(() => (invoice ? buildViewModel(invoice) : null), [invoice]);

  const exportMutation = useMutation({
    mutationFn: (id: number) => invoiceService.exportInvoice(id),
    onSuccess: (blob, id) => {
      downloadBlob(blob, `hoa-don-${id}.pdf`);
      message.success('Đang tải hóa đơn');
    },
    onError: () => message.error('Xuất hóa đơn thất bại'),
  });

  const handleExport = () => {
    if (invoiceId) exportMutation.mutate(invoiceId);
  };

  const loading = isLoading || isFetching;

  return (
    <Modal
      title={null}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={920}
      className="invoice-excel-detail-modal"
      destroyOnClose
    >
      {loading && (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
        </div>
      )}

      {!loading && isError && (
        <Alert type="error" showIcon message="Không tải được chi tiết hóa đơn." style={{ marginBottom: 16 }} />
      )}

      {!loading && invoice && vm && (
        <div className="invoice-excel-detail">
          <h3 className="invoice-excel-detail__section-title">Thông tin khách hàng</h3>
          <div className="invoice-excel-detail__grid">
            <div>
              <Bullet label="Họ tên:" value={vm.customerName} />
              <Bullet label="Địa chỉ:" value={vm.address} />
              <Bullet label="CCCD / MST:" value={vm.taxId} />
              <Bullet label="Mã số khách hàng:" value={vm.customerCode} />
            </div>
            <div>
              <Bullet label="Số hộ sử dụng:" value={vm.household} />
              <Bullet label="Định mức (khối):" value={vm.quotaBlocks} />
              <Bullet label="Khối - Số đọc:" value={vm.meterReading} />
              <Bullet label="Thời gian sử dụng:" value={vm.usageRange} />
            </div>
          </div>

          <h3 className="invoice-excel-detail__section-title">Chi tiết</h3>
          <div className="invoice-excel-detail__detail-block">
            <div className="invoice-excel-detail__detail-headers">
              <span>Số đọc tháng này</span>
              <span>Số đọc tháng trước</span>
              <span>Số lượng tiêu thụ (M3)</span>
              <span>Định mức tiêu thụ</span>
              <span>Đơn giá</span>
              <span>Thành tiền</span>
            </div>
            <div className="invoice-excel-detail__detail-values">
              <span>{vm.currRead}</span>
              <span>{vm.prevRead}</span>
              <span>{vm.consumptionM3}</span>
              <span>{vm.consQuota}</span>
              <span>{vm.unitPriceDisplay}</span>
              <span>{vm.lineAmountDisplay}</span>
            </div>
          </div>

          <div className="invoice-excel-detail__summary">
            <div className="invoice-excel-detail__summary-row">
              <span className="invoice-excel-detail__summary-label">Cộng tiền hàng:</span>
              <span className="invoice-excel-detail__summary-value">{vm.subGoodsDisplay}</span>
            </div>
            <div className="invoice-excel-detail__summary-row">
              <span className="invoice-excel-detail__summary-label">Thuế suất ({vm.taxRate}%):</span>
              <span className="invoice-excel-detail__summary-value">{vm.taxDisplay}</span>
            </div>
            <div className="invoice-excel-detail__summary-row">
              <span className="invoice-excel-detail__summary-label">
                Phí BVMT đối với nước thải SH 10%:
              </span>
              <span className="invoice-excel-detail__summary-value">{vm.envDisplay}</span>
            </div>
            <div className="invoice-excel-detail__summary-row invoice-excel-detail__summary-row--total">
              <span className="invoice-excel-detail__summary-label">Tổng tiền thanh toán:</span>
              <span className="invoice-excel-detail__summary-value">{vm.totalDisplay}</span>
            </div>
          </div>

          <div className="invoice-excel-detail__words">
            <span className="invoice-excel-detail__summary-label">Số tiền bằng chữ:</span>
            <span className="invoice-excel-detail__words-value">{vm.amountInWords}</span>
          </div>

          <div className="invoice-excel-detail__actions">
            <Button
              type="primary"
              size="large"
              icon={<SendOutlined />}
              className="invoice-excel-detail__btn-export"
              loading={exportMutation.isPending}
              onClick={handleExport}
            >
              Xuất hóa đơn
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

function Bullet({ label, value }: { label: string; value: string }) {
  return (
    <div className="invoice-excel-detail__bullet-row">
      <span className="invoice-excel-detail__bullet" aria-hidden>
        ●
      </span>
      <span className="invoice-excel-detail__label">{label}</span>
      <span className="invoice-excel-detail__value">{value}</span>
    </div>
  );
}

export default InvoiceExcelDetailModal;
